import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { Species, SubSpecies } from '../../../api/dtos/dtos';
import { DEFAULT_SPECIES_CONTEXT, SpeciesContext } from '../../../contexts/data/SpeciesContext';
import { IHasChildren } from '../../common/types/IHasChildren';
import useGetSubSpecies from '../../../hooks/data/useGetSubSpecies';

interface ISpeciesProviderProps extends IHasChildren {}

const SpeciesProvider: FC<ISpeciesProviderProps> = ({ children }) => {
    const { species: speciesName } = useParams();

    const speciesInfo = useMemo(() => {
        if (!speciesName) return undefined;

        const cleaned = speciesName
            .replace('scan', '')
            .replace('alert', '')
            .toLowerCase();

        const key = Object.keys(Species).find(
            k => k.toLowerCase() === cleaned
        ) as keyof typeof Species | undefined;

        if (!key) return undefined;

        return {
            key: key.toLowerCase(),
            species: Species[key]
        };
    }, [speciesName]);

    const subSpeciesRequest = useMemo(
        () => speciesInfo ? { speciesId: speciesInfo.species } : undefined,
        [speciesInfo]
    );

    const { data: subSpecies } = useGetSubSpecies(
        subSpeciesRequest,
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    const context = useMemo(() => {
        if (!speciesInfo) {
            return { species: { ...DEFAULT_SPECIES_CONTEXT } };
        }

        if (subSpecies && subSpecies?.length > 0 && !subSpecies.find(x => x.id == -1)) {
            subSpecies.unshift({ id: -1, value: "All", rank: undefined, obsolete: false } as SubSpecies);
        }
        return {
            species: {
                species: speciesInfo.species,
                key: speciesInfo.key,
                subSpecies: subSpecies
            }
        };
    }, [speciesInfo, subSpecies]);

    return (
        <SpeciesContext.Provider value={context}>
            {children}
        </SpeciesContext.Provider>
    );
};

export default SpeciesProvider;
